import {Card, Modal, Spinner} from 'react-bootstrap'
import {useEffect, useState} from 'react'
import {useLazyQuery} from '@apollo/client'
import {GET_AGENTS_NEW_CLIENTS} from '../../../gql/queries/reportsQuery'
import './AdvisorsCompletedPoliciesTable.css'

interface IUser {
  id: string
  firstName: string
  lastName: string
  email: string
}

interface IAgentNewClientsData {
  user: IUser
  newClientsCount: number
  totalClientsCount: number
  newClientsPercentage: number
  rank: number
}

interface IResAgentsNewClients {
  agentsNewClients: IAgentNewClientsData[]
}

interface INewClientsTableProps {
  dateFrom: string
  dateTo: string
  dateCriteria: 'submitted' | 'effective'
  carrier?: string
  stateEu?: string
  policyType?: string
  status?: string[]
  agent?: string[]
  paymentStatus?: string[]
  advisorPaid?: boolean
  loading?: boolean
  nonCanceledStatusCodes?: string[]
  onOpenPolicies?: (extraFilters: Record<string, unknown>) => void
  refreshTrigger?: number
}

type SortField = 'agent' | 'newClients' | 'totalClients' | 'newClientsPercentage'
type SortOrder = 'asc' | 'desc'

export const NewClientsTable = ({
  dateFrom,
  dateTo,
  dateCriteria,
  carrier,
  stateEu,
  policyType,
  status,
  agent,
  paymentStatus,
  advisorPaid,
  loading: parentLoading,
  nonCanceledStatusCodes,
  onOpenPolicies,
  refreshTrigger,
}: INewClientsTableProps) => {
  const [showModal, setShowModal] = useState(false)

  const openPoliciesForAgent = (entry: IAgentNewClientsData) =>
    onOpenPolicies?.({
      user: [entry.user.id],
      status: status && status.length > 0 ? status : nonCanceledStatusCodes,
    })
  const [sortField, setSortField] = useState<SortField>('newClients')
  const [sortOrder, setSortOrder] = useState<SortOrder>('desc')

  const [getTopAgents, {data, loading}] = useLazyQuery<IResAgentsNewClients>(
    GET_AGENTS_NEW_CLIENTS,
    {fetchPolicy: 'no-cache'}
  )

  const [getAllAgents, {data: fullData, loading: loadingFull}] =
    useLazyQuery<IResAgentsNewClients>(GET_AGENTS_NEW_CLIENTS, {fetchPolicy: 'no-cache'})

  useEffect(() => {
    getTopAgents({
      variables: {
        dateFrom,
        dateTo,
        dateCriteria,
        carrier: carrier ? [carrier] : undefined,
        stateEu: stateEu ? [stateEu] : undefined,
        policyType: policyType ? [policyType] : undefined,
        status,
        agent,
        paymentStatus,
        advisorPaid,
        limit: 10,
      },
    })
  }, [dateFrom, dateTo, dateCriteria, carrier, stateEu, policyType, status, agent, paymentStatus, advisorPaid, refreshTrigger, getTopAgents])

  const handleShowMore = () => {
    getAllAgents({
      variables: {
        dateFrom,
        dateTo,
        dateCriteria,
        carrier: carrier ? [carrier] : undefined,
        stateEu: stateEu ? [stateEu] : undefined,
        policyType: policyType ? [policyType] : undefined,
        status,
        agent,
        paymentStatus,
        advisorPaid,
        limit: 100,
      },
    })
    setShowModal(true)
  }

  const handleSort = (field: SortField) => {
    if (sortField === field) {
      setSortOrder(sortOrder === 'asc' ? 'desc' : 'asc')
    } else {
      setSortField(field)
      setSortOrder(field === 'agent' ? 'asc' : 'desc')
    }
  }

  const agentsData = data?.agentsNewClients || []
  const fullAgentsData = fullData?.agentsNewClients || []

  const sortedFullAgents = [...fullAgentsData].sort((a, b) => {
    if (sortField === 'agent') {
      const nameA = `${a.user.firstName} ${a.user.lastName}`
      const nameB = `${b.user.firstName} ${b.user.lastName}`
      const cmp = nameA.localeCompare(nameB)
      return sortOrder === 'asc' ? cmp : -cmp
    } else if (sortField === 'newClients') {
      const cmp = a.newClientsCount - b.newClientsCount
      return sortOrder === 'asc' ? cmp : -cmp
    } else if (sortField === 'totalClients') {
      const cmp = a.totalClientsCount - b.totalClientsCount
      return sortOrder === 'asc' ? cmp : -cmp
    } else {
      const cmp = a.newClientsPercentage - b.newClientsPercentage
      return sortOrder === 'asc' ? cmp : -cmp
    }
  })

  const getSortIcon = (field: SortField) => {
    if (sortField !== field) return <i className='bi bi-arrow-down-up ms-1 text-muted'></i>
    return sortOrder === 'asc' ? (
      <i className='bi bi-arrow-up ms-1'></i>
    ) : (
      <i className='bi bi-arrow-down ms-1'></i>
    )
  }

  const getInitials = (firstName: string, lastName: string) =>
    `${firstName.charAt(0)}${lastName.charAt(0)}`.toUpperCase()

  const renderTable = (rows: IAgentNewClientsData[], showEmail = false) => (
    <div className='table-responsive'>
      <table className='table table-row-dashed table-row-gray-300 align-middle gs-0 gy-2'>
        <thead
          style={
            showEmail
              ? {position: 'sticky', top: 0, backgroundColor: 'white', zIndex: 1}
              : undefined
          }
        >
          <tr className='fw-bold text-muted'>
            <th className='ps-0' style={{width: '5%'}}></th>
            <th
              style={{width: showEmail ? '15%' : '35%', cursor: 'pointer'}}
              onClick={() => handleSort('agent')}
            >
              Agent {getSortIcon('agent')}
            </th>
            {showEmail && <th className='text-center' style={{width: '15%'}}>Email</th>}
            <th
              className='text-center'
              style={{width: '15%', cursor: 'pointer'}}
              onClick={() => handleSort('newClients')}
            >
              New Client Policies {getSortIcon('newClients')}
            </th>
            <th
              className='text-center'
              style={{width: '15%', cursor: 'pointer'}}
              onClick={() => handleSort('totalClients')}
            >
              Total Policies {getSortIcon('totalClients')}
            </th>
            <th
              className='text-center'
              style={{width: '15%', cursor: 'pointer'}}
              onClick={() => handleSort('newClientsPercentage')}
            >
              New % {getSortIcon('newClientsPercentage')}
            </th>
          </tr>
        </thead>
        <tbody>
          {rows.map((entry, index) => (
            <tr key={entry.user.id}>
              <td className='ps-0 text-center'>
                <span className='text-muted fw-bold fs-7'>{index + 1}</span>
              </td>
              <td>
                <div className='d-flex align-items-center'>
                  <div
                    className='rounded-circle bg-light-success d-flex align-items-center justify-content-center me-3'
                    style={{width: 30, height: 30}}
                  >
                    <span className='text-success fw-bold fs-7'>
                      {getInitials(entry.user.firstName, entry.user.lastName)}
                    </span>
                  </div>
                  <span className='text-gray-800 fw-bold fs-7'>
                    {entry.user.firstName} {entry.user.lastName}
                  </span>
                </div>
              </td>
              {showEmail && (
                <td className='text-center'>
                  <span className='text-muted fs-8'>{entry.user.email}</span>
                </td>
              )}
              <td className='text-center'>
                <span
                  className='fw-bold text-success fs-6'
                  style={onOpenPolicies ? {cursor: 'pointer', textDecoration: 'underline dotted'} : undefined}
                  title="Shows all policies for this agent in the period — cannot isolate 'new client only' yet"
                  onClick={() => openPoliciesForAgent(entry)}
                >
                  {entry.newClientsCount}
                </span>
              </td>
              <td className='text-center'>
                <span
                  className='fw-bold text-gray-800 fs-7'
                  style={onOpenPolicies ? {cursor: 'pointer', textDecoration: 'underline dotted'} : undefined}
                  onClick={() => openPoliciesForAgent(entry)}
                >
                  {entry.totalClientsCount}
                </span>
              </td>
              <td className='text-center'>
                <span className='badge badge-light-success fs-8'>
                  {entry.newClientsPercentage.toFixed(1)}%
                </span>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  )

  return (
    <>
      <Card className='card-flush h-100'>
        <Card.Header className='border-0 pt-5'>
          <h3 className='card-title align-items-start flex-column'>
            <span className='card-label fw-bold fs-3 mb-1'>
              New Client Policies by Agent (Top 10)
            </span>
            <span className='text-muted fw-semibold fs-7'>
              Policies whose holder has no prior policy before this period
            </span>
          </h3>
          <div className='card-toolbar'>
            <button
              className='btn btn-sm btn-light-success'
              onClick={handleShowMore}
              disabled={loading || parentLoading}
            >
              <i className='bi bi-plus-lg me-1'></i>
              See more
            </button>
          </div>
        </Card.Header>
        <Card.Body className='pt-0'>
          {(loading || parentLoading) && (
            <div className='d-flex justify-content-center align-items-center' style={{minHeight: 350}}>
              <Spinner animation='border' />
            </div>
          )}
          {!loading && !parentLoading && agentsData.length === 0 && (
            <div className='d-flex justify-content-center align-items-center' style={{minHeight: 350}}>
              <p className='text-muted'>No new clients data for the selected period</p>
            </div>
          )}
          {!loading && !parentLoading && agentsData.length > 0 && renderTable(agentsData)}
        </Card.Body>
      </Card>

      <Modal show={showModal} onHide={() => setShowModal(false)} size='xl' dialogClassName='modal-90w'>
        <Modal.Header closeButton>
          <Modal.Title>New Client Policies by Agent</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {loadingFull && (
            <div className='d-flex justify-content-center align-items-center' style={{minHeight: 200}}>
              <Spinner animation='border' />
            </div>
          )}
          {!loadingFull && fullAgentsData.length === 0 && (
            <div className='text-center text-muted py-5'>No data available</div>
          )}
          {!loadingFull && fullAgentsData.length > 0 && (
            <div style={{maxHeight: '60vh', overflowY: 'auto'}}>
              {renderTable(sortedFullAgents, true)}
            </div>
          )}
        </Modal.Body>
        <Modal.Footer>
          <button className='btn btn-secondary' onClick={() => setShowModal(false)}>
            Close
          </button>
        </Modal.Footer>
      </Modal>
    </>
  )
}
